<script lang="ts">
  export interface FormChangeProfileImageProps {
    tenantUser: TenantUser
    loadingIds?: string[]
    class?: HTMLAttributes['class']
  }
</script>

<script setup lang="ts">
  import { type HTMLAttributes, ref, useTemplateRef, watch } from 'vue'
  import type {
    ChangeTenantUserProfileImageRequest,
    TenantUser
  } from '@gat-ship/types'
  import { useTenantUserDetailContext } from '../../composables/useTenantUserDetail'
  import { useProfileImageUpload } from '../../composables/useProfileImageUpload'
  import { useUser } from '@gat-ship/user'
  import {
    Avatar,
    AvatarFallback,
    AvatarImage,
    Button,
    cn,
    ModalContent,
    useAppLoading
  } from '@gat-ship/vue-components'
  import { IconUpload } from '@gat-ship/icons'
  import { globalUtils } from '@gat-ship/utils'
  import { IconLoading } from '@gat-ship/icons'

  const tenantUserProvider = useTenantUserDetailContext()!
  const { uploadProfileImage } = useProfileImageUpload()

  const props = defineProps<FormChangeProfileImageProps>()

  const requestPayload = ref<Partial<ChangeTenantUserProfileImageRequest>>({
    userId: undefined,
    processedImageUploadId: undefined
  })

  watch(
    () => props.tenantUser,
    async (tenantUser) => {
      requestPayload.value.userId = tenantUser.userId
    },
    {
      deep: true,
      immediate: true
    }
  )

  function isValidRequestPayload(
    payload: Partial<ChangeTenantUserProfileImageRequest>
  ): payload is ChangeTenantUserProfileImageRequest {
    if (payload.userId) {
      return true
    }
    return false
  }

  defineEmits<{
    (e: 'saving'): void
  }>()

  async function sendFormData() {
    const payload = requestPayload.value
    if (isValidRequestPayload(payload)) {
      await tenantUserProvider.commands.changeProfileImage(payload)
    }
  }

  const fileInput = useTemplateRef('fileInput')

  const user = useUser()

  const previewUrl = ref<string | undefined>(undefined)

  const { setLoadingId, removeLoadingId, isLoading } = useAppLoading()

  async function onFileSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0]
    if (!file) {
      return
    }
    try {
      setLoadingId('upload-file-btn')
      setLoadingId(props.loadingIds)
      const result = await uploadProfileImage(
        file,
        user.id.value,
        globalUtils.functions.uuid()
      )
      requestPayload.value.processedImageUploadId =
        result.processedImageUploadId
      previewUrl.value = result.processedImagePreviewUrl
      await sendFormData()
    } finally {
      removeLoadingId('upload-file-btn')
      removeLoadingId(props.loadingIds)
    }
  }

  function onUploadPhotoClicked(event?: MouseEvent) {
    event?.preventDefault()
    event?.stopPropagation()
    fileInput.value?.click()
    console.log('onUploadPhotoClicked', event)
  }

  function resetForm() {
    requestPayload.value.processedImageUploadId = undefined
    previewUrl.value = undefined
  }

  defineExpose({
    sendFormData,
    onUploadPhotoClicked,
    resetForm
  })
</script>
<template>
  <ModalContent :class="cn('flex flex-col gap-4', props.class)">
    <div class="flex items-center gap-2">
      <Avatar class="size-14">
        <IconLoading
          v-if="isLoading('upload-file-btn')"
          class="text-muted-foreground"
        />
        <template v-else>
          <AvatarImage v-if="previewUrl" :src="previewUrl" />
          <AvatarFallback> </AvatarFallback>
        </template>
      </Avatar>
      <Button variant="outline" @click.stop="onUploadPhotoClicked($event)">
        <IconUpload class="size-4" />
        Upload Photo
      </Button>
      <input
        ref="fileInput"
        type="file"
        accept="image/*"
        class="hidden"
        @change="onFileSelected"
        @click.stop
      />
    </div>
  </ModalContent>
</template>
